import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import "./confirm.css";

const ConfirmContext = createContext(null);

/* Promise-based replacement for window.confirm.
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Delete this film?" }))) return;
 *
 * Unlike window.confirm this is stylable, testable, doesn't block the main
 * thread, and is reachable by keyboard and screen reader. */
export function ConfirmProvider({ children }) {
  const [request, setRequest] = useState(null);
  const resolverRef = useRef(null);
  const confirmButtonRef = useRef(null);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setRequest({
        title: "Are you sure?",
        message: "",
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
        destructive: true,
        ...options,
      });
    });
  }, []);

  const settle = useCallback((result) => {
    setRequest(null);
    resolverRef.current?.(result);
    resolverRef.current = null;
  }, []);

  useEffect(() => {
    if (!request) return undefined;

    confirmButtonRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") settle(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [request, settle]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}

      {request && (
        <div className="confirm-backdrop" onClick={() => settle(false)}>
          <div
            className="confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            aria-describedby={request.message ? "confirm-message" : undefined}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 className="confirm-dialog__title" id="confirm-title">{request.title}</h2>

            {request.message && (
              <p className="confirm-dialog__message" id="confirm-message">{request.message}</p>
            )}

            <div className="confirm-dialog__actions">
              <button
                type="button"
                className="confirm-dialog__cancel"
                onClick={() => settle(false)}
              >
                {request.cancelLabel}
              </button>

              <button
                type="button"
                ref={confirmButtonRef}
                className={
                  request.destructive
                    ? "confirm-dialog__confirm confirm-dialog__confirm--destructive"
                    : "confirm-dialog__confirm"
                }
                onClick={() => settle(true)}
              >
                {request.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used inside a <ConfirmProvider>");
  }
  return context;
}
